/* specs/05-product.md */

class VelourProduct extends HTMLElement {
  connectedCallback() {
    this.mainGallery = this.querySelector('[data-gallery-main]');
    this.buyBox = this.querySelector('[data-buybox]');
    this.addToBagBtn = this.querySelector('[data-add-to-bag]');
    this.buyNowBtn = this.querySelector('[data-buy-now]');
    this.quantityInput = this.querySelector('[data-product-quantity]');
    this.sellingPlanId = null;

    this.setupGallery();
    this.setupPackSize();
    this.setupPurchaseType();
    this.setupActions();
    this.setupPincode();
    this.setupStickyBar();
  }

  currentVariantId() {
    const selected = this.querySelector('.product-buybox__pack-option.is-selected');
    return selected ? selected.dataset.variantId : this.addToBagBtn?.dataset.variantId;
  }

  setupGallery() {
    const slides = this.querySelectorAll('.product-gallery__slide');
    const thumbs = this.querySelectorAll('[data-gallery-thumb]');
    const dots = this.querySelectorAll('[data-gallery-dot]');

    const showSlide = (index) => {
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      thumbs.forEach((thumb, i) => thumb.classList.toggle('is-selected', i === index));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => showSlide(parseInt(thumb.dataset.galleryThumb, 10)));
    });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.galleryDot, 10);
        showSlide(index);
        if (this.mainGallery) {
          const slide = slides[index];
          if (slide) this.mainGallery.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        }
      });
    });

    if (this.mainGallery && window.matchMedia('(max-width: 759px)').matches) {
      this.mainGallery.addEventListener(
        'scroll',
        debounce(() => {
          const index = Math.round(this.mainGallery.scrollLeft / this.mainGallery.clientWidth);
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        }, 100)
      );
    }
  }

  setupPackSize() {
    const options = this.querySelectorAll('.product-buybox__pack-option');
    if (!options.length) return;

    options.forEach((option) => {
      option.addEventListener('click', () => {
        if (option.disabled) return;
        options.forEach((o) => o.classList.remove('is-selected'));
        option.classList.add('is-selected');

        const price = parseInt(option.dataset.price, 10);
        const compareAt = parseInt(option.dataset.compareAt, 10) || 0;
        const units = parseInt(option.dataset.units, 10) || 1;
        const variantId = option.dataset.variantId;

        const singleUnit = Array.from(options).find((o) => parseInt(o.dataset.units, 10) === 1);
        const basePerUnit = singleUnit
          ? parseInt(singleUnit.dataset.price, 10)
          : Math.min(...Array.from(options).map((o) => parseInt(o.dataset.price, 10) / (parseInt(o.dataset.units, 10) || 1)));
        const perUnit = price / units;
        const saveTotal = Math.round((basePerUnit - perUnit) * units);

        const priceRow = this.querySelector('.product-buybox__price-row .price-row__current');
        const compareRow = this.querySelector('.product-buybox__price-row .price-row__compare');
        if (priceRow) priceRow.textContent = velourFormatMoney(price);
        if (compareRow) {
          if (compareAt > price) {
            compareRow.textContent = velourFormatMoney(compareAt);
            compareRow.hidden = false;
          } else {
            compareRow.hidden = true;
          }
        }

        const note = this.querySelector('[data-pack-note]');
        if (note) {
          if (saveTotal > 0) {
            note.textContent = `${velourFormatMoney(perUnit)} each · save ${velourFormatMoney(saveTotal)}`;
            note.hidden = false;
          } else {
            note.hidden = true;
          }
        }

        if (this.addToBagBtn) this.addToBagBtn.dataset.variantId = variantId;
        if (this.buyNowBtn) this.buyNowBtn.dataset.variantId = variantId;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);

        const stickyPrice = this.querySelector('[data-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = velourFormatMoney(price);
      });
    });
  }

  setupPurchaseType() {
    const subscription = this.querySelector('[data-purchase-subscription]');
    const onetime = this.querySelector('[data-purchase-onetime]');
    if (!subscription) return;

    const sync = () => {
      this.sellingPlanId = subscription.checked ? subscription.dataset.sellingPlanId : null;
    };
    subscription.addEventListener('change', sync);
    onetime?.addEventListener('change', sync);
  }

  setupActions() {
    this.addToBagBtn?.addEventListener('click', () => {
      const variantId = this.currentVariantId();
      if (!variantId) return;
      const quantity = parseInt(this.quantityInput?.value, 10) || 1;
      this.addToBagBtn.disabled = true;
      window.VelourCart.addItem(variantId, quantity, { sellingPlanId: this.sellingPlanId })
        .then(() => {
          this.addToBagBtn.disabled = false;
        })
        .catch((error) => {
          console.error(error);
          this.addToBagBtn.disabled = false;
        });
    });

    const buyNowHandler = () => {
      const variantId = this.currentVariantId();
      if (!variantId) return;
      const quantity = parseInt(this.quantityInput?.value, 10) || 1;
      const item = { id: variantId, quantity: quantity };
      if (this.sellingPlanId) item.selling_plan = this.sellingPlanId;

      fetch(`${window.routes.cart_add_url}.js`, { ...fetchConfig(), body: JSON.stringify({ items: [item] }) })
        .then((response) => response.json())
        .then((data) => {
          if (data.status) throw new Error(data.description || 'Could not add to bag');
          window.location.href = '/checkout';
        })
        .catch((error) => console.error(error));
    };

    this.buyNowBtn?.addEventListener('click', buyNowHandler);
    this.querySelector('[data-sticky-buy-now]')?.addEventListener('click', buyNowHandler);
  }

  setupPincode() {
    const input = this.querySelector('[data-pincode-input]');
    const button = this.querySelector('[data-pincode-check]');
    const result = this.querySelector('[data-pincode-result]');
    if (!input || !button || !result) return;

    // Heuristic only: metro pincode prefixes ship in 3-4 days, everywhere else in 5-7.
    // Replace with a real courier/logistics lookup if the merchant needs precise ETAs.
    const metroPrefixes = ['11', '40', '41', '38', '50', '56', '57', '58', '60', '70', '20'];

    button.addEventListener('click', () => {
      const pincode = input.value.trim();
      if (!/^\d{6}$/.test(pincode)) {
        result.textContent = 'Enter a valid 6-digit pincode.';
        return;
      }
      const isMetro = metroPrefixes.includes(pincode.slice(0, 2));
      const days = isMetro ? 4 : 7;
      const date = new Date();
      date.setDate(date.getDate() + days);
      const formatted = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
      result.textContent = `Arrives by ${formatted}`;
    });
  }

  setupStickyBar() {
    const bar = this.querySelector('[data-sticky-bar]');
    if (!bar || !this.buyNowBtn) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          bar.hidden = false;
          if (entry.isIntersecting) {
            bar.removeAttribute('data-visible');
          } else {
            bar.setAttribute('data-visible', '');
          }
        });
      },
      { rootMargin: '0px 0px -1px 0px' }
    );
    observer.observe(this.buyNowBtn);
  }
}

customElements.define('velour-product', VelourProduct);
